import { forwardRef, ReactElement, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Divider,
  SvgIcon,
  Collapse,
} from '@mui/material';

import { ReactComponent as IconSettings } from 'assets/images/icon_settings.svg';
import { ReactComponent as ChevronDown } from 'assets/images/ic_chevron_down.svg';
import { ReactComponent as Logout } from 'assets/images/logout.svg';
import OrganisationList from './OrganisationList';
import ComingSoonBadge from './ComingSoon';
import { useDispatch, useSelector } from 'react-redux';
import { PaginationActions } from 'state/pagination/Action';
import { DefaultPaginationState } from 'state/pagination/Reducer';
import { BaseApplicationState } from 'state/baseapplication/Reducer';

export interface IAppSidebarInnerRef {
  logoutPortal: any;
}

export enum EMenuType {
  NAVIGATION = 'navigation',
  ONCLICK = 'onClick',
}

export interface IMenuItems {
  path: string;
  title: string;
  icon: ReactElement;
  key: string;
  aclKey: string;
  navigationType: EMenuType;
  superFlag: any;
  childrenMenus: IMenuItems[];
  comingSoon?: boolean;
}

export interface IAppSidebar {
  children: React.ReactNode;
  menus: IMenuItems[];
  ref: React.Ref<IAppSidebarInnerRef>;
  onMenuChange?: (menu: IMenuItems) => void;
  onUserSettingsClick: () => void;
  onUserLoggingOut: () => void;
  pathname: string;
  onOrgChange: () => void;
}
const AppSidebar: React.FC<IAppSidebar> = forwardRef((props): ReactElement => {
  const {
    children,
    menus,
    onMenuChange,
    onUserSettingsClick,
    onUserLoggingOut,
    onOrgChange,
  } = props;

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState('');
  const { activeOrg, orgACLConfig }: BaseApplicationState = useSelector(
    (state: any) => state.baseAppState
  );
  const [menuList, setMenuList] = useState<IMenuItems[]>([]);
  // const isChildActive = (path: string): boolean => {
  //   const tMenu = menus.find((m) => m.path === path);
  //   if (tMenu) {
  //     return tMenu.childrenMenus.some((m) =>
  //       location.pathname.startsWith(m.path)
  //     );
  //   }
  //   return false;
  // };

  const setActiveParentMenu = (path: string): void => {
    const menu = menus.find(
      (m) => m.childrenMenus.findIndex((cm) => cm.path === path) > -1
    );
    if (menu) {
      setActiveMenu(menu.path);
    }
  };

  const hasACLAccess = (aclKey: string): boolean =>
    Object.values(orgACLConfig ?? {}).some(
      (aclList) => Array.isArray(aclList) && aclList.includes(aclKey)
    );

  useEffect(() => {
    const filteredMenuList = menus
      .filter((menu) => hasACLAccess(menu.aclKey))
      .map((menu) => ({
        ...menu,
        childrenMenus: menu.childrenMenus?.filter((child) =>
          hasACLAccess(child.aclKey)
        ),
      }));

    setMenuList(filteredMenuList);
  }, [orgACLConfig, menus, activeOrg]);

  useEffect(() => {
    setActiveParentMenu(location.pathname);
    // console.log(location.pathname);
  }, [location.pathname]);

  // UI Elements
  return (
    <Box className="sidebar">
      <OrganisationList onOrgChange={onOrgChange} />

      <Box className="side-menu-v2">
        <Box className="menu-container">
          <Box className="menus" role="navigation">
            {menuList.map((menu: IMenuItems) => (
              <>
                {menu.navigationType === EMenuType.NAVIGATION && (
                  <Box
                    className="menu-link"
                    onClick={() => {
                      if (menu.childrenMenus?.length > 0) {
                        if (menu.path === activeMenu) {
                          setActiveMenu('');
                        } else {
                          setActiveMenu(menu.path);
                        }
                      } else {
                        navigate(menu.path);
                      }
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack direction="row" className="menu-navigation-stack">
                        {menu.icon}
                        <Typography className={`menu ${menu.key}`}>
                          <Box>{menu.title}</Box>
                        </Typography>
                      </Stack>

                      {menu?.childrenMenus?.length > 0 && (
                        <Box className="dropdown-chevron">
                          {activeMenu !== menu.path && (
                            <SvgIcon
                              component={ChevronDown}
                              inheritViewBox
                              className="icon-drop-down-color down"
                              fontSize="inherit"
                              htmlColor="#3D3C42"
                            />
                          )}
                          {activeMenu === menu.path && (
                            <SvgIcon
                              component={ChevronDown}
                              inheritViewBox
                              className="icon-drop-down-color up"
                              fontSize="inherit"
                              htmlColor="#3D3C42"
                            />
                          )}
                        </Box>
                      )}
                    </Stack>
                  </Box>
                )}
                {menu.navigationType === EMenuType.ONCLICK && (
                  <Typography
                    className="menu cursor-pointer"
                    onClick={() => onMenuChange?.(menu)}
                  >
                    {menu.title}
                    {/* <Box className="menu-icon">{menu.icon}</Box> */}
                  </Typography>
                )}
                {menu.childrenMenus && (
                  <Collapse
                    in={activeMenu === menu.path}
                    role="region"
                    id="panel-search-by-dashboard-sidebar"
                    aria-labelledby="search-by-dashboard-sidebar"
                  >
                    <Box className="menus child-menu">
                      {Array.isArray(menu.childrenMenus) &&
                        menu.childrenMenus?.length > 0 &&
                        menu.childrenMenus.map((menu: IMenuItems) => (
                          <>
                            {menu.navigationType === EMenuType.NAVIGATION && (
                              <NavLink
                                className={({ isActive }) =>
                                  isActive ? 'active menu-link' : 'menu-link'
                                }
                                to={menu.path}
                                onClick={() => {
                                  dispatch(
                                    PaginationActions.setPagination({
                                      ...DefaultPaginationState,
                                    })
                                  );
                                }}
                              >
                                <Stack
                                  direction="row"
                                  className="menu-navigation-stack"
                                >
                                  {menu.icon}
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                  >
                                    <Typography className={`menu ${menu.key}`}>
                                      {menu.title}
                                    </Typography>
                                    {menu.comingSoon && <ComingSoonBadge />}
                                  </Stack>
                                </Stack>
                              </NavLink>
                            )}
                            {menu.navigationType === EMenuType.ONCLICK && (
                              <Typography
                                className="menu cursor-pointer"
                                onClick={() => onMenuChange?.(menu)}
                              >
                                {menu.title}
                              </Typography>
                            )}
                          </>
                        ))}
                    </Box>
                  </Collapse>
                )}
              </>
            ))}
          </Box>
          <Divider className="divider-menu" />
          <Box className="menus sub-menu" role="navigation">
            {orgACLConfig.settings?.includes('settings') && (
              <NavLink
                className={({ isActive }) =>
                  isActive ? 'active menu-link' : 'menu-link'
                }
                to="settings"
                onClick={() => {
                  setActiveMenu('/settings');
                  onUserSettingsClick();
                }}
              >
                <Stack direction="row" className="menu-navigation-stack">
                  <SvgIcon
                    component={IconSettings}
                    inheritViewBox
                    className="menu-icon"
                    fontSize="inherit"
                  />
                  <Typography className="menu">Settings</Typography>
                </Stack>
              </NavLink>
            )}
            <Box
              className="menu-link"
              onClick={() => {
                onUserLoggingOut();
              }}
            >
              <Stack direction="row" className="menu-navigation-stack">
                <SvgIcon
                  component={Logout}
                  inheritViewBox
                  className="menu-icon"
                  fontSize="inherit"
                />
                <Typography className="menu">Logout</Typography>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>
      {children}
    </Box>
  );
});

export default AppSidebar;
